<template>
<div class="k__video-player">
  <video
    ref="videoElement"
    :id="videoId"
    controls
    class="k_video_player_tag video-js vjs-default-skin"
    :style="{
      width: w ? `${w}px` : '100%',
      height: h ? `${h}px` : '100%'
    }"
    preload="metadata"
    v-bind="options"
  >
    <source :src="src" type="video/mp4" v-if="!isDashSource">
  </video>

  <div class="loading player-btn" v-show="playerBtnShow && !sourceLoading && !videoError" @click="play">
    <van-icon name="play-circle-o" color="#fff" size="60" />
  </div>

  <span class="loading" v-if="sourceLoading">
    <van-loading />
  </span>
  <div class="loading" v-if="videoError" role="status">
    <van-button @click="init">Unable to load video. Try again</van-button>
  </div>
</div>
</template>

<script setup lang="ts">
import type { VideoHTMLAttributes } from 'vue';

const props = defineProps<{
  src: string,
  autoPlay?: boolean,
  w?: number,
  h?: number,
  options?: VideoHTMLAttributes
}>()

const { $asyncLoadJs } = useNuxtApp()
const isDashSource = computed(() => /\.mpd(?:[?#]|$)/i.test(props.src))
const playerBtnShow = ref<boolean>(true)
const sourceLoading = ref(false)
const videoError = ref(false)
const videoElement = ref<HTMLVideoElement>()
let initializing = false
let disposed = false
const videoId = `k_video_${Date.now()}_${Math.floor(Math.random() * 10 )}${Math.floor(Math.random() * 10 )}`

function loadStyles(){
  if (document.getElementById('kilimall_videojs-css')) return
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.type = "text/css";
  link.id = 'kilimall_videojs-css'
  link.href = 'https://img.kilimall.com/c/h5/css/videojs.min.css'
  const head = document.getElementsByTagName("head")[0];
  head.appendChild(link);
}

const loadSource = async() => {
  useDashVideoJSLoadingState().value = 'loading'
  loadStyles()

  await Promise.all([
    $asyncLoadJs('https://img.kilimall.com/c/h5/js/vidoe.min.js', { id: 'kilimall_video', ready: () => typeof window.videojs === 'function' }),
    $asyncLoadJs('https://img.kilimall.com/c/h5/js/dash.all.min.js', { id: 'kilimall_dash', ready: () => !!(window as any).dashjs })
  ])
  // The adapter executes against videojs/dash; parallel insertion races its dependencies.
  await $asyncLoadJs('https://img.kilimall.com/c/h5/js/videojs-dash.min.js', { id: 'kilimall_videojs-dash' })
}

let player = shallowRef<any>(null)
const init = async () => {
  if (initializing || disposed) return
  initializing = true
  videoError.value = false
  sourceLoading.value = true
  const src = props.src
  try {
    if (!src) throw new Error('Video source is required')
    if (isDashSource.value) {
      await loadSource()
      await nextTick()
      if (disposed || !videoElement.value) return
      player.value = window.videojs(videoElement.value)
      player.value.src({ src, type: 'application/dash+xml' })
      useDashVideoJSLoadingState().value = 'loaded'
    } else {
      await nextTick()
      if (disposed) return
      player.value = videoElement.value
    }
    if (props.autoPlay) await play()
  } catch {
    videoError.value = true
    useDashVideoJSLoadingState().value = 'pending'
  } finally {
    sourceLoading.value = false
    initializing = false
  }
}

const play = async () => {
  if (!player.value) return
  try {
    await player.value.play()
    playerBtnShow.value = false
  } catch {
    playerBtnShow.value = true
  }
}

onMounted(init)

const pause = () => {
  playerBtnShow.value = false
  if(player.value) {
    player.value.pause()
  }
}
defineExpose({ player, pause, play })
onBeforeUnmount(() => {
  disposed = true
  pause()
  player.value?.dispose?.()
  player.value = null
})
</script>

<style lang="scss" scoped>
.k__video-player{
  width: 100%;
  height: 100%;
  position: relative;
}
.k__video-player::v-deep(.vjs-big-play-button) { display: none; }
.player-btn{
  
}
.loading{
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  @include center();
}
</style>
